// @flow

import React, { PureComponent, type Node, Fragment } from "react";
import { Collapse } from "react-collapse";
import { bindAll, noop } from "lodash";
import type { UnaryFn } from "@tvg/types/Functional";
import Icon from "@fdr/static-ui/Icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import { arrowDown } from "@fdr/static-ui/Icons/icons";
import {
  Row,
  ExpandableHeader,
  IconContainer,
  ClickableArea,
  RowLineBreak
} from "./styled-components";

export type State = { isOpen: boolean };

export type Props = {
  /**
   * Header Component
   */
  header: Node,
  /**
   * Content component
   */
  children: Node,
  /**
   * Expandable Row
   */
  isExpandable: boolean,
  /**
   * Open/Close Row
   */
  isOpen: boolean,
  /**
   * Callback when expand.
   */
  onExpand: UnaryFn<boolean, mixed>,
  /**
   * Classname to style the component
   */
  className: string,
  /**
   * should render Icon
   */
  renderIcon: boolean,
  isLastTrack: boolean,
  qaLabel: string,
  fixedHeight?: number
};

export default class ExpandableRow extends PureComponent<Props, State> {
  static defaultProps = {
    header: null,
    children: null,
    isExpandable: true,
    isOpen: false,
    onExpand: noop,
    className: "",
    withLetter: null,
    renderIcon: true,
    isLastTrack: false,
    qaLabel: "",
    fixedHeight: undefined
  };

  constructor(props: Props): void {
    super(props);
    this.state = {
      isOpen: props.isOpen
    };
    bindAll(this, "onExpand");
  }

  static getDerivedStateFromProps(nextProps: Props, state: State) {
    if (nextProps.isOpen !== undefined && nextProps.isOpen !== state.isOpen) {
      return { ...state, isOpen: nextProps.isOpen };
    }
    return state;
  }

  onExpand(): void {
    if (this.props.isExpandable) {
      this.setState(
        {
          isOpen: !this.state.isOpen
        },
        () => {
          this.props.onExpand(this.state.isOpen);
        }
      );
    }
  }

  renderRowLineBreak() {
    const windowPage = window.location.pathname.split("/")[1];

    return (
      !this.props.isLastTrack &&
      (windowPage !== "racetracks" ||
        (windowPage === "racetracks" && this.props.isOpen)) && <RowLineBreak />
    );
  }

  render() {
    return (
      <Fragment>
        <Row
          className={this.props.className}
          renderIcon={this.props.renderIcon}
          qaLabel={this.props.qaLabel || "expandableRow-collapser"}
        >
          <ExpandableHeader
            renderIcon={this.props.renderIcon}
            onTouchStart={noop}
          >
            <ClickableArea
              renderIcon={this.props.renderIcon}
              onClick={this.onExpand}
              data-qa-label="expandableClickableArea"
            >
              {this.props.header}
            </ClickableArea>
            {this.props.isExpandable && this.props.renderIcon && (
              <IconContainer isOpen={this.state.isOpen}>
                <Icon
                  icon={arrowDown}
                  size={24}
                  color={buildColor("blue", "D1")}
                />
              </IconContainer>
            )}
          </ExpandableHeader>
          {this.props.isExpandable && (
            <Collapse
              isOpened={this.state.isOpen && !!this.props.children}
              springConfig={{ stiffness: 200, damping: 25 }}
              fixedHeight={this.props.fixedHeight}
            >
              {this.props.children}
            </Collapse>
          )}
        </Row>
        {this.renderRowLineBreak()}
      </Fragment>
    );
  }
}
